import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { HashIcon, SearchIcon, CloseIcon, AlertIcon } from "../lib/icons.js";
import { Input } from "../ui/Input.js";
import { Button } from "../ui/Button.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { getArticleDetail } from "../lib/api.js";
import { useDebouncedValue } from "../lib/useDebounce.js";
import type { ArticleDetail } from "../lib/types.js";

/**
 * Obrazovka E — hledání dílu podle article number.
 *
 * Přesná shoda vyvolá API (/api/catalog/articles/:number), při nálezu
 * tlačítko "Otevřít detail" nebo okamžitý redirect (po stisku Enter).
 */
export function PartSearchPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQ = searchParams.get("q") ?? "";
  const [q, setQ] = useState(initialQ);
  const debouncedQ = useDebouncedValue(q.trim(), 350);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ArticleDetail | null>(null);
  const [notFound, setNotFound] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (debouncedQ !== searchParams.get("q")) {
      const next = new URLSearchParams(searchParams);
      if (debouncedQ) next.set("q", debouncedQ);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }
  }, [debouncedQ, searchParams, setSearchParams]);

  useEffect(() => {
    if (!debouncedQ || debouncedQ.length < 3) {
      setResult(null);
      setNotFound(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    getArticleDetail(debouncedQ)
      .then((res) => !cancelled && setResult(res))
      .catch(() => {
        if (!cancelled) {
          setResult(null);
          setNotFound(true);
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [debouncedQ]);

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (result) {
      navigate(`/part/${encodeURIComponent(result.article.articleNumber)}`);
    }
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8">
        <h1 className="text-3xl font-semibold tracking-tight text-zinc-900">
          Hledat díl podle čísla
        </h1>
        <p className="mt-1 text-zinc-500">
          Zadejte přesné article number, např. z dokladu nebo servisního manuálu.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6">
        <Input
          autoFocus
          sizeVariant="lg"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="např. 58032034000"
          iconLeft={<HashIcon size={20} />}
          iconRight={
            q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Smazat"
                className="hover:text-zinc-600"
              >
                <CloseIcon size={18} />
              </button>
            ) : undefined
          }
          spellCheck={false}
          autoCapitalize="characters"
        />
      </form>

      {loading && <LoadingBlock label="Hledám…" />}

      {!loading && !result && !notFound && debouncedQ.length === 0 && (
        <EmptyState
          icon={<SearchIcon size={28} />}
          title="Začněte zadáním článkového čísla"
          description="Stačí i část čísla — vyhledávač najde přesnou shodu."
        />
      )}

      {!loading && !result && !notFound && debouncedQ.length > 0 && debouncedQ.length < 3 && (
        <EmptyState
          icon={<SearchIcon size={28} />}
          title="Zadejte aspoň 3 znaky"
          description="Kratší dotazy neprohledáváme, abychom vás nezavalili."
        />
      )}

      {!loading && notFound && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 flex gap-4">
          <AlertIcon size={24} className="text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-semibold text-amber-900">Díl nenalezen</h3>
            <p className="text-sm text-amber-800 mt-1">
              Article <span className="font-mono">{debouncedQ}</span> v naší databázi
              není. Zkontrolujte číslo nebo hledejte přes model.
            </p>
            <Link to="/search/model">
              <Button variant="secondary" size="sm" className="mt-3">
                Hledat přes model
              </Button>
            </Link>
          </div>
        </div>
      )}

      {!loading && result && <PartPreview article={result} />}
    </div>
  );
}

function PartPreview({ article }: { article: ArticleDetail }): JSX.Element {
  return (
    <Link
      to={`/part/${encodeURIComponent(article.article.articleNumber)}`}
      className="group block bg-white rounded-xl border border-zinc-200 shadow-card hover:shadow-card-hover hover:border-ktm-orange-100 transition-all p-5 animate-fade-in"
    >
      <div className="flex items-start gap-5">
        <div className="w-24 h-24 bg-zinc-50 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
          {article.article.imageUrl ? (
            <img
              src={article.article.imageUrl}
              alt={article.article.name ?? ""}
              className="max-w-full max-h-full object-contain"
            />
          ) : (
            <HashIcon size={28} className="text-zinc-300" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-zinc-500 font-mono">
            {article.article.articleNumber}
          </div>
          <h2 className="text-lg font-semibold text-zinc-900 mt-1 group-hover:text-ktm-orange transition-colors">
            {article.article.name ?? "Bez názvu"}
          </h2>
          <div className="mt-2 text-sm text-zinc-500">
            Kompatibilní modely:{" "}
            <span className="font-medium text-zinc-700">
              {article.compatibleCount}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}
